import {
  cloneElement,
  isValidElement,
  useState,
  useRef,
  useMemo,
  useEffect,
  useLayoutEffect,
  useCallback,
  type DragEvent,
  type ClipboardEvent,
  type CSSProperties,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthUser } from '@alchemiesolns/user-access';
import { isAuthenticated } from '@alchemiesolns/product-registration';
import { startBillingPortalSession, useLicense, type LicenseHookState } from '@alchemiesolns/store-api';
import { BuildSection } from './BuildSection';
import { ExportSection } from './ExportSection';
import { ExportableScheme } from './ExportableScheme';
import { SingleMoleculeExport } from './SingleMoleculeExport';
import { MoleculeEditToolbar } from './MoleculeEditToolbar';
import { PreviewArrowPanel } from './PreviewArrowPanel';
import { SketcherOverlay } from './SketcherOverlay';
import { Button, CheckIcon, ClipboardIcon, Toast } from '@alchemiesolns/ui';
import { ConfirmDialog } from '@alchemiesolns/ui';
import { TextInputDialog } from './TextInputDialog';
import type { OverlayTextHorizontalAlign } from './OverlayTextSvg';
import { StructureTreePanel } from './StructureTreePanel';
import type { ArrowType } from './ArrowSection';
import { useSchemeImport } from '../hooks/useSchemeImport';
import { useSchemeExport } from '../hooks/useSchemeExport';
import { useToast } from '../hooks/useToast';
import { useReactionSchemeState } from '../hooks/useReactionSchemeState';
import { useAltTextGeneration } from '../hooks/useAltTextGeneration';
import { useCharacterPalette } from '../hooks/useCharacterPalette';
import { useMoleculeTransfer } from '../hooks/useMoleculeTransfer';
import { useSketcherModal } from '../hooks/useSketcherModal';
import { computeNormalizedViewBoxes, computeSingleViewBox } from '../utils/viewBoxNormalization';
import { useFileImport } from '../hooks/useFileImport';
import {
  PREVIEW_DISPLAY_SCALE,
  VIEWBOX_CONFIG,
  BUILD_MOLECULE_TEMPLATE_START_ALIGN,
  VIEWBOX_EXTRA_USER_PADDING_UNITS,
} from '../constants/reactionScheme';
import {
  SCHEME_FONT_SIZE_DEFAULT,
  SCHEME_ZOOM_PERCENT_DEFAULT,
  clampSchemeZoomPercent,
  computeEffectiveSchemeDisplayZoom,
  formatSchemeFontSizeConversion,
} from '../utils/schemeZoom';
import {
  DEFAULT_MOLECULE_EXPORT_DIMENSIONS,
  DEFAULT_REACTION_EXPORT_DIMENSIONS,
  MOLECULE_LAYOUT_BASE,
  getUniformSvgScale,
} from '../utils/exportLayout';
import {
  computeReactionStageLayout,
} from '../utils/reactionExportLayout';
import { getStableReactionArrowClusterScale } from '../utils/reactionArrowScale';
import {
  measureReactionMoleculeBoundsInExportSpace,
  type AxisAlignedRect,
} from '../utils/measureReactionMoleculeBounds';
import { computePreviewArrowDockCenterX } from '../utils/previewArrowDock';
import { INTENT_QUERY_KEY } from '../auth/navigation';
import { useTrialUsage } from '../hooks/useTrialUsage';
import { LicenseStatusBanner } from './LicenseStatusBanner';
import { PRODUCT_ID } from '../constants/product';
import { TRIAL_USAGE_LIMIT } from '../constants/trialUsage';
import './ReactionScheme.css';

interface ReactionConfig {
  reactant: string;
  product: string;
  reagents: string;
  conditions: string;
  arrowType: ArrowType;
  reactantViewBox?: string;
  productViewBox?: string;
}

interface MoleculeConfig {
  config: string;
}

/**
 * Single knob for center-preview gesture feel.
 * >1 = faster/more aggressive zoom, <1 = slower/more precise.
 */
const LICENSE_STATUS_BANNER_HIDE_STATUSES = new Set<LicenseHookState['status']>(['idle', 'loading', 'active']);

const PREVIEW_ZOOM_SENSITIVITY = 1.15;
const EXPORT_WITHOUT_ALT_CONFIRM_KEY = 'reaction-scheme-export-without-alt-confirmed';
const RESET_BUILD_CONFIRM_SKIP_KEY = 'reaction-scheme-reset-build-skip-confirm';

/** Sync with ReactionScheme.css: compact arrow bar `max-height` media query for short windows. */
const ARROW_CONTROLS_COMPACT_MEDIA = '(max-height: 780px)';
const ARROW_CONTROLS_COMPACT_PANEL_WIDTH_PX = 200;

interface PendingExportRequest {
  filename: string;
  format: 'svg' | 'png';
}

type TemplateMode = 'reaction' | 'molecule';

export function ReactionScheme() {
  const location = useLocation();
  const navigate = useNavigate();
  const bypassBilling = import.meta.env.VITE_BYPASS_BILLING === 'true';
  const billingConfigured = Boolean(import.meta.env.VITE_API_BASE?.trim());
  const { user, ready: authReady } = useAuthUser();
  const hasLicenseSession = isAuthenticated();
  const licenseState = useLicense(PRODUCT_ID, { enabled: authReady && !!user });
  const hasActiveLicense =
    hasLicenseSession ||
    licenseState.status === 'active' ||
    licenseState.status === 'past_due';
  const trialUsage = useTrialUsage({
    uid: user?.uid,
    hasLicense: hasActiveLicense,
    bypassBilling,
    authReady,
  });
  const SIDE_PANEL_WIDTH_MIN = 240;
  const SIDE_PANEL_WIDTH_MAX = 720;
  const SIDE_PANEL_WIDTH_DEFAULT = 320;

  // Dev mode state - disabled in production builds
  const [isDevMode] = useState(false);
  const [selectedPasteTarget, setSelectedPasteTarget] = useState<'reactant' | 'product' | 'arrow' | 'molecule' | null>(null);

  // Zoom state from interactive molecule preview; used to keep arrow/text scale aligned with
  // molecule apparent scale. Font size is fixed to the default reference and only zoom changes.
  const schemeFontSize = SCHEME_FONT_SIZE_DEFAULT;
  const [schemeZoomPercent, setSchemeZoomPercent] = useState(SCHEME_ZOOM_PERCENT_DEFAULT);
  const schemeZoomPercentRef = useRef(SCHEME_ZOOM_PERCENT_DEFAULT);
  const schemeZoomUiToBaseRatioRef = useRef(1);

  useEffect(() => {
    schemeZoomPercentRef.current = schemeZoomPercent;
  }, [schemeZoomPercent]);

  useEffect(() => {
    try {
      setSkipMissingAltTextConfirm(
        localStorage.getItem(EXPORT_WITHOUT_ALT_CONFIRM_KEY) === '1'
      );
    } catch {
      setSkipMissingAltTextConfirm(false);
    }
  }, []);

  const handleSchemeZoomPercentChange = useCallback((p: number) => {
    const nextPercent = clampSchemeZoomPercent(SCHEME_FONT_SIZE_DEFAULT, p);
    schemeZoomPercentRef.current = nextPercent;
    setSchemeZoomPercent(nextPercent);
  }, []);

  const handleSchemeZoomSettled = useCallback(
    (
      percentCommitted: number,
      _before: { percent: number },
    ) => {
      const nextPercent = clampSchemeZoomPercent(SCHEME_FONT_SIZE_DEFAULT, percentCommitted);
      schemeZoomPercentRef.current = nextPercent;
      setSchemeZoomPercent(nextPercent);
    },
    [],
  );

  /**
   * Base zoom from committed controls state only (safe for early layout math before
   * displayed/fit-adjusted zoom is derived).
   */
  const schemeDisplayZoomBase = useMemo(
    () => computeEffectiveSchemeDisplayZoom(schemeFontSize, schemeZoomPercent),
    [schemeFontSize, schemeZoomPercent],
  );

  // Core state management hook
  const schemeState = useReactionSchemeState();
  const {
    templateMode,
    reactantConfig,
    productConfig,
    topText,
    bottomText,
    arrowType,
    reactantViewBox,
    productViewBox,
    altText,
    systemPrompt,
    contextPrompt,
    selectedContextOption,
    jsonInput,
    reactantOverlayText,
    productOverlayText,
    reactantOverlayPosition,
    productOverlayPosition,
    reactantOverlayTextAlign,
    productOverlayTextAlign,
    setTemplateMode,
    setReactantConfig,
    setProductConfig,
    setTopText,
    setBottomText,
    setArrowType,
    setReactantViewBox,
    setProductViewBox,
    setAltText,
    setSystemPrompt,
    setContextPrompt,
    setSelectedContextOption,
    setJsonInput,
    setReactantOverlayText,
    setProductOverlayText,
    setReactantOverlayPosition,
    setProductOverlayPosition,
    setReactantOverlayTextAlign,
    setProductOverlayTextAlign,
    clearState,
    resetMoleculeArrowProduct,
  } = schemeState;

  /** Same notion of “has content” as template switch — used to skip confirm when there is nothing to lose. */
  const hasBuildDataToLose = useMemo(
    () =>
      Boolean(
        reactantConfig ||
          productConfig ||
          reactantOverlayText ||
          productOverlayText ||
          reactantViewBox ||
          productViewBox ||
          topText.trim() ||
          bottomText.trim() ||
          altText.trim(),
      ),
    [
      reactantConfig,
      productConfig,
      reactantOverlayText,
      productOverlayText,
      reactantViewBox,
      productViewBox,
      topText,
      bottomText,
      altText,
    ],
  );

  // Text input dialog state
  const [textDialogOpen, setTextDialogOpen] = useState(false);
  const [textDialogTarget, setTextDialogTarget] = useState<'reactant' | 'product'>('reactant');
  const [exportWidth, setExportWidth] = useState(DEFAULT_REACTION_EXPORT_DIMENSIONS.width);
  const [exportHeight, setExportHeight] = useState(DEFAULT_REACTION_EXPORT_DIMENSIONS.height);
  const [sidePanelWidth, setSidePanelWidth] = useState(SIDE_PANEL_WIDTH_DEFAULT);
  const [lockAspectRatio, setLockAspectRatio] = useState(false);
  const viewBoxScaleMode = 'fixed' as const;
  const [previewFitScale, setPreviewFitScale] = useState(1);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [showMoleculeBounds, setShowMoleculeBounds] = useState(false);
  const [moleculeSlotBounds, setMoleculeSlotBounds] = useState<{
    reactant: AxisAlignedRect | null;
    product: AxisAlignedRect | null;
  } | null>(null);
  const [pendingExportRequest, setPendingExportRequest] = useState<PendingExportRequest | null>(null);
  const [pendingTemplateMode, setPendingTemplateMode] = useState<TemplateMode | null>(null);
  const [resetBuildConfirmOpen, setResetBuildConfirmOpen] = useState(false);
  const [skipResetBuildConfirm, setSkipResetBuildConfirm] = useState(false);
  const [dontAskResetBuildAgain, setDontAskResetBuildAgain] = useState(false);
  const [skipMissingAltTextConfirm, setSkipMissingAltTextConfirm] = useState(false);
  const [dontAskExportWithoutAltAgain, setDontAskExportWithoutAltAgain] = useState(false);
  /** Short window: dock arrow panel beside the arrow slot (`ARROW_CONTROLS_COMPACT_MEDIA`). */
  const [compactArrowViewport, setCompactArrowViewport] = useState(false);
  /** Horizontal center in scaled frame px — only meaningful when compact + arrow selected + measured. */
  const [compactArrowDockCenterX, setCompactArrowDockCenterX] = useState<number | null>(null);

  useEffect(() => {
    try {
      setSkipResetBuildConfirm(localStorage.getItem(RESET_BUILD_CONFIRM_SKIP_KEY) === '1');
    } catch {
      setSkipResetBuildConfirm(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(ARROW_CONTROLS_COMPACT_MEDIA);
    const sync = () => setCompactArrowViewport(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (templateMode === 'reaction' && selectedPasteTarget === 'molecule') {
      setSelectedPasteTarget(null);
    }
  }, [templateMode, selectedPasteTarget]);

  useEffect(() => {
    const handleDocumentPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // Controls bar subtree (excluding portaled descendants).
      if (target.closest('.preview-selected-controls-bar')) {
        return;
      }

      // Arrow type listbox renders in createPortal(.., document.body).
      if (target.closest('.arrow-type-menu')) {
        return;
      }

      if (!target.closest('.preview-paste-slot')) {
        setSelectedPasteTarget(null);
      }
    };

    document.addEventListener('pointerdown', handleDocumentPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handleDocumentPointerDown, true);
    };
  }, []);

  // Refs
  const exportRef = useRef<HTMLDivElement>(null);
  const reagentsInputRef = useRef<HTMLInputElement>(null);
  const conditionsInputRef = useRef<HTMLInputElement>(null);
  const previewViewportRef = useRef<HTMLDivElement>(null);
  const pointerStartRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchStartRef = useRef<{ distance: number; zoom: number } | null>(null);
  const previewZoomRef = useRef(1);
  const zoomRafRef = useRef<number | null>(null);
  const pendingZoomRef = useRef<number | null>(null);
  const arrowCompactDockRafRef = useRef<number | null>(null);
  const sidePanelResizeRef = useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
    el: HTMLDivElement;
  } | null>(null);

  // Toast notification hook
  const { showToast, toastMessage, show: showToastMessage } = useToast();

  const showTrialConsumptionToast = useCallback(
    (remainingAfter: number) => {
      if (remainingAfter > 0) {
        showToastMessage(
          `Trial action recorded. ${remainingAfter} of ${TRIAL_USAGE_LIMIT} free trial actions remaining.`,
        );
      } else {
        showToastMessage(
          `You've used all ${TRIAL_USAGE_LIMIT} free trial actions. Subscribe for full access.`,
        );
      }
    },
    [showToastMessage],
  );

  const lastAppDataSyncErrorRef = useRef<string | null>(null);
  useEffect(() => {
    const msg = trialUsage.appDataSyncError;
    if (!msg) {
      lastAppDataSyncErrorRef.current = null;
      return;
    }
    if (lastAppDataSyncErrorRef.current === msg) return;
    lastAppDataSyncErrorRef.current = msg;
    showToastMessage(`Trial usage sync issue. Verify pricing and access at checkout. (${msg})`);
  }, [trialUsage.appDataSyncError, showToastMessage]);

  // Alt text generation hook
  const altTextGeneration = useAltTextGeneration({
    templateMode,
    reactantConfig,
    productConfig,
    reactantOverlayText,
    productOverlayText,
    topText,
    bottomText,
    arrowType,
    systemPrompt,
    selectedContextOption,
    altText,
    setAltText,
  });

  const routeToUpgradeForAction = useCallback((intent: 'generate' | 'export') => {
    const params = new URLSearchParams();
    params.set('return', '/app');
    params.set(INTENT_QUERY_KEY, intent);
    navigate(`/upgrade?${params.toString()}`, {
      state: { backgroundLocation: location },
    });
  }, [location, navigate]);

  const routeToUpgradeBuy = useCallback(() => {
    const params = new URLSearchParams();
    params.set('return', '/app');
    params.set(INTENT_QUERY_KEY, 'subscribe');
    navigate(`/upgrade?${params.toString()}`, {
      state: { backgroundLocation: location },
    });
  }, [location, navigate]);

  const openBillingPortal = useCallback(async () => {
    if (!billingConfigured) {
      routeToUpgradeBuy();
      return;
    }
    try {
      const origin = window.location.origin;
      const base = import.meta.env.BASE_URL;
      await startBillingPortalSession({
        productId: PRODUCT_ID,
        returnUrl: `${origin}${base}app`,
      });
    } catch (e) {
      showToastMessage(e instanceof Error ? e.message : 'Could not open billing portal.');
    }
  }, [billingConfigured, routeToUpgradeBuy, showToastMessage]);

  const showLicenseStatusBanner =
    !bypassBilling &&
    authReady &&
    Boolean(user) &&
    !LICENSE_STATUS_BANNER_HIDE_STATUSES.has(licenseState.status);

  const handleGenerateAltText = useCallback(async () => {
    if (!bypassBilling && authReady) {
      const ls = licenseState.status;
      if (ls === 'revoked') {
        showToastMessage(
          'Your Piph Author license is no longer active. Renew or subscribe to generate alt text.',
        );
        routeToUpgradeForAction('generate');
        return;
      }
      if (ls === 'expired') {
        showToastMessage(
          'Your Piph Author license is no longer active. Renew or subscribe to generate alt text.',
        );
        routeToUpgradeBuy();
        return;
      }
      if (ls === 'error') {
        showToastMessage(
          "We couldn't verify your license. Check your connection and try again, or open Upgrade and use Refresh license.",
        );
        return;
      }
    }
    if (!bypassBilling && !isAuthenticated()) {
      if (!trialUsage.trialAllowsAction) {
        showToastMessage(
          'You have used every action included in the free trial. Subscribe or redeem a license to generate alt text.',
        );
        routeToUpgradeForAction('generate');
        return;
      }
    }
    const ok = await altTextGeneration.generateAltText();
    if (ok && !bypassBilling && !isAuthenticated()) {
      const left = await trialUsage.consumeTrialUse();
      if (left !== null) {
        showTrialConsumptionToast(left);
      }
    }
  }, [
    altTextGeneration,
    authReady,
    bypassBilling,
    licenseState.status,
    routeToUpgradeBuy,
    routeToUpgradeForAction,
    showToastMessage,
    showTrialConsumptionToast,
    trialUsage,
  ]);

  // Character palette hook
  const characterPalette = useCharacterPalette({
    reagentsInputRef,
    conditionsInputRef,
    topText,
    bottomText,
    setTopText,
    setBottomText,
    showToastMessage,
  });

  const clearAltTextIfPresent = useCallback(() => {
    if (altText.trim().length > 0) {
      setAltText('');
    }
  }, [altText, setAltText]);

  // Config updates: clear overlay; viewBox is reset only from Build Structure (blank sketcher) or Add Text.
  const handleReactantConfigChange = useCallback((config: string) => {
    clearAltTextIfPresent();
    setReactantConfig(config);
    setReactantOverlayText('');
    setReactantOverlayPosition(null);
    setReactantOverlayTextAlign(null);
  }, [
    clearAltTextIfPresent,
    setReactantConfig,
    setReactantOverlayPosition,
    setReactantOverlayText,
    setReactantOverlayTextAlign,
  ]);

  const handleProductConfigChange = useCallback((config: string) => {
    clearAltTextIfPresent();
    setProductConfig(config);
    setProductOverlayText('');
    setProductOverlayPosition(null);
    setProductOverlayTextAlign(null);
  }, [
    clearAltTextIfPresent,
    setProductConfig,
    setProductOverlayPosition,
    setProductOverlayText,
    setProductOverlayTextAlign,
  ]);

  const handleTopTextChange = useCallback((text: string) => {
    clearAltTextIfPresent();
    setTopText(text);
  }, [clearAltTextIfPresent, setTopText]);

  const handleBottomTextChange = useCallback((text: string) => {
    clearAltTextIfPresent();
    setBottomText(text);
  }, [clearAltTextIfPresent, setBottomText]);

  const handleArrowTypeChange = useCallback((type: ArrowType) => {
    clearAltTextIfPresent();
    setArrowType(type);
  }, [clearAltTextIfPresent, setArrowType]);

  const handleResetBuildContent = useCallback(() => {
    clearAltTextIfPresent();
    resetMoleculeArrowProduct();
    setSelectedPasteTarget(null);

    const defaultPercent = clampSchemeZoomPercent(SCHEME_FONT_SIZE_DEFAULT, SCHEME_ZOOM_PERCENT_DEFAULT);
    schemeZoomPercentRef.current = defaultPercent;
    setSchemeZoomPercent(defaultPercent);

    if (zoomRafRef.current !== null) {
      cancelAnimationFrame(zoomRafRef.current);
      zoomRafRef.current = null;
    }
    pendingZoomRef.current = null;
    previewZoomRef.current = 1;
    setPreviewZoom(1);
    const viewport = previewViewportRef.current;
    if (viewport) {
      viewport.scrollLeft = 0;
      viewport.scrollTop = 0;
    }
  }, [clearAltTextIfPresent, resetMoleculeArrowProduct]);

  const handleResetBuildRequest = useCallback(() => {
    if (skipResetBuildConfirm || !hasBuildDataToLose) {
      handleResetBuildContent();
      return;
    }
    setDontAskResetBuildAgain(false);
    setResetBuildConfirmOpen(true);
  }, [skipResetBuildConfirm, hasBuildDataToLose, handleResetBuildContent]);

  const cancelResetBuild = useCallback(() => {
    setResetBuildConfirmOpen(false);
    setDontAskResetBuildAgain(false);
  }, []);

  const confirmResetBuild = useCallback(() => {
    if (dontAskResetBuildAgain) {
      try {
        localStorage.setItem(RESET_BUILD_CONFIRM_SKIP_KEY, '1');
      } catch {
        /* ignore persistence failures */
      }
      setSkipResetBuildConfirm(true);
    }
    setResetBuildConfirmOpen(false);
    setDontAskResetBuildAgain(false);
    handleResetBuildContent();
  }, [dontAskResetBuildAgain, handleResetBuildContent]);

  // Molecule transfer hook
  const moleculeTransfer = useMoleculeTransfer({
    reactantConfig,
    productConfig,
    setReactantConfig: handleReactantConfigChange,
    setProductConfig: handleProductConfigChange,
    showToast: showToastMessage,
  });

  // Sketcher modal hook
  const sketcherModal = useSketcherModal({
    reactantConfig,
    productConfig,
    setReactantConfig: handleReactantConfigChange,
    setProductConfig: handleProductConfigChange,
  });

  /**
   * Reactant/molecule config last saved from **Build Structure** (blank sketcher).
   * Matches current `reactantConfig` → molecular template uses starter horizontal layout; never serialized.
   */
  const buildStarterReactantConfigRef = useRef<string | null>(null);

  const clearBuildLayoutStarters = useCallback(() => {
    buildStarterReactantConfigRef.current = null;
  }, []);

  const handleSketcherSave = useCallback(
    (config: string) => {
      sketcherModal.save(config);
      if (!sketcherModal.loadExisting && sketcherModal.editingTarget) {
        const t = sketcherModal.editingTarget;
        if (t === 'reactant' || t === 'molecule') {
          buildStarterReactantConfigRef.current = config;
          setReactantViewBox(null);
        } else if (t === 'product') {
          setProductViewBox(null);
        }
      }
    },
    [
      sketcherModal.save,
      sketcherModal.loadExisting,
      sketcherModal.editingTarget,
      setReactantViewBox,
      setProductViewBox,
    ]
  );

  // Import handling (confirmation dialog, global drop, state reconstruction)
  const schemeImport = useSchemeImport(
    { reactantConfig, productConfig },
    {
      setTemplateMode,
      setReactantConfig,
      setProductConfig,
      setTopText,
      setBottomText,
      setArrowType,
      setAltText,
      setReactantViewBox,
      setProductViewBox,
      setReactantOverlayText,
      setProductOverlayText,
      setReactantOverlayPosition,
      setProductOverlayPosition,
      setReactantOverlayTextAlign,
      setProductOverlayTextAlign,
      setSchemeZoomPercent,
      onSchemeReplaced: clearBuildLayoutStarters,
    },
    showToastMessage,
  );

  const reactantImport = useFileImport({
    onConfigImport: handleReactantConfigChange,
    onAlchemieImport: schemeImport.handleAlchemieImport,
    onImportError: (msg) => { showToastMessage(`Import failed: ${msg}`); },
  });

  const productImport = useFileImport({
    onConfigImport: handleProductConfigChange,
    onAlchemieImport: schemeImport.handleAlchemieImport,
    onImportError: (msg) => { showToastMessage(`Import failed: ${msg}`); },
  });

  const moleculeImport = useFileImport({
    onConfigImport: handleReactantConfigChange,
    onAlchemieImport: schemeImport.handleAlchemieImport,
    onImportError: (msg) => { showToastMessage(`Import failed: ${msg}`); },
  });

  // Handle removing config (clears config, overlay text, and viewBox)
  const handleRemoveReactantConfig = useCallback(() => {
    clearAltTextIfPresent();
    buildStarterReactantConfigRef.current = null;
    setReactantConfig('');
    setReactantOverlayText('');
    setReactantOverlayPosition(null);
    setReactantOverlayTextAlign(null);
    setReactantViewBox(null);
  }, [
    clearAltTextIfPresent,
    setReactantConfig,
    setReactantOverlayPosition,
    setReactantOverlayText,
    setReactantOverlayTextAlign,
    setReactantViewBox,
  ]);

  const handleRemoveProductConfig = useCallback(() => {
    clearAltTextIfPresent();
    setProductConfig('');
    setProductOverlayText('');
    setProductOverlayPosition(null);
    setProductOverlayTextAlign(null);
    setProductViewBox(null);
  }, [
    clearAltTextIfPresent,
    setProductConfig,
    setProductOverlayPosition,
    setProductOverlayText,
    setProductOverlayTextAlign,
    setProductViewBox,
  ]);

  // Handle removing overlay text only
  const handleRemoveReactantText = useCallback(() => {
    clearAltTextIfPresent();
    setReactantOverlayText('');
    setReactantOverlayPosition(null);
    setReactantOverlayTextAlign(null);
  }, [
    clearAltTextIfPresent,
    setReactantOverlayPosition,
    setReactantOverlayText,
    setReactantOverlayTextAlign,
  ]);

  const handleRemoveProductText = useCallback(() => {
    clearAltTextIfPresent();
    setProductOverlayText('');
    setProductOverlayPosition(null);
    setProductOverlayTextAlign(null);
  }, [
    clearAltTextIfPresent,
    setProductOverlayPosition,
    setProductOverlayText,
    setProductOverlayTextAlign,
  ]);

  // Handle adding text to reactant/product
  const handleAddReactantText = useCallback(() => {
    setTextDialogTarget('reactant');
    setTextDialogOpen(true);
  }, []);

  const handleAddProductText = useCallback(() => {
    setTextDialogTarget('product');
    setTextDialogOpen(true);
  }, []);

  const handleSaveText = useCallback((text: string) => {
    clearAltTextIfPresent();
    if (textDialogTarget === 'reactant') {
      setReactantOverlayText(text);
      setReactantViewBox(null);
      // Overlay replaces the reactant slot (molecule or reaction): one content type so the toolbar shows a single remove action.
      if (text.trim()) {
        buildStarterReactantConfigRef.current = null;
        setReactantConfig('');
      }
      if (!text) {
        setReactantOverlayTextAlign(null);
      } else if (reactantOverlayTextAlign == null) {
        setReactantOverlayTextAlign(templateMode === 'molecule' ? 'middle' : 'end');
      }
    } else {
      setProductOverlayText(text);
      setProductViewBox(null);
      if (text.trim()) {
        setProductConfig('');
      }
      if (!text) {
        setProductOverlayTextAlign(null);
      } else if (productOverlayTextAlign == null) {
        setProductOverlayTextAlign('start');
      }
    }
    setTextDialogOpen(false);
  }, [
    textDialogTarget,
    templateMode,
    setProductConfig,
    reactantOverlayTextAlign,
    productOverlayTextAlign,
    setReactantOverlayText,
    setProductOverlayText,
    setReactantOverlayTextAlign,
    setProductOverlayTextAlign,
    setReactantViewBox,
    setReactantConfig,
    setProductViewBox,
    clearAltTextIfPresent,
  ]);

  const handleCloseTextDialog = useCallback(() => {
    setTextDialogOpen(false);
  }, []);

  const aspectRatio = useMemo(
    () => (exportHeight > 0 ? exportWidth / exportHeight : 1),
    [exportWidth, exportHeight]
  );

  const handleExportWidthChange = useCallback((newWidth: number) => {
    if (!Number.isFinite(newWidth) || newWidth <= 0) return;
    const clamped = Math.max(64, Math.round(newWidth));
    setExportWidth(clamped);
    if (lockAspectRatio) {
      setExportHeight(Math.max(64, Math.round(clamped / aspectRatio)));
    }
  }, [lockAspectRatio, aspectRatio]);

  const handleExportHeightChange = useCallback((newHeight: number) => {
    if (!Number.isFinite(newHeight) || newHeight <= 0) return;
    const clamped = Math.max(64, Math.round(newHeight));
    setExportHeight(clamped);
    if (lockAspectRatio) {
      setExportWidth(Math.max(64, Math.round(clamped * aspectRatio)));
    }
  }, [lockAspectRatio, aspectRatio]);

  const applyTemplateChange = useCallback((mode: TemplateMode) => {
    clearState();
    clearBuildLayoutStarters();
    setShowMoleculeBounds(false);
    setMoleculeSlotBounds(null);
    setSelectedPasteTarget(null);
    setTemplateMode(mode);
    if (mode === 'molecule') {
      setExportWidth(DEFAULT_MOLECULE_EXPORT_DIMENSIONS.width);
      setExportHeight(DEFAULT_MOLECULE_EXPORT_DIMENSIONS.height);
    } else {
      setExportWidth(DEFAULT_REACTION_EXPORT_DIMENSIONS.width);
      setExportHeight(DEFAULT_REACTION_EXPORT_DIMENSIONS.height);
    }
  }, [
    clearState,
    clearBuildLayoutStarters,
    setTemplateMode,
  ]);

  // Handle template mode change - clear all state (with in-house confirm dialog)
  const handleTemplateChange = useCallback((mode: TemplateMode) => {
    if (mode === templateMode) return;

    if (hasBuildDataToLose) {
      setPendingTemplateMode(mode);
      return;
    }

    applyTemplateChange(mode);
  }, [templateMode, hasBuildDataToLose, applyTemplateChange]);

  const cancelTemplateChange = useCallback(() => {
    setPendingTemplateMode(null);
  }, []);

  const confirmTemplateChange = useCallback(() => {
    if (!pendingTemplateMode) return;
    applyTemplateChange(pendingTemplateMode);
    setPendingTemplateMode(null);
  }, [pendingTemplateMode, applyTemplateChange]);

  /**
   * Layout viewBoxes must use the **same pixel aspect ratio** as each molecule `<svg>` (slot size).
   * Using a fixed 192×144 reference while the slot is ~299×240 makes `meet` scaling fill the
   * viewport width with no horizontal slack, so `preserveAspectRatio` xMin/xMax cannot pin content.
   */
  const {
    normalizedViewBoxes,
    reactantIndividualViewBox,
    productIndividualViewBox,
  } = useMemo(() => {
    if (templateMode !== 'reaction') {
      return {
        normalizedViewBoxes: null as ReturnType<typeof computeNormalizedViewBoxes> | null,
        reactantIndividualViewBox: null as string | null,
        productIndividualViewBox: null as string | null,
      };
    }

    // Never use persisted `reactantViewBox` (user pan/zoom) here. It would re-run
    // normalization on every reactant drag and change `productLayoutViewBox`, resetting
    // the product InteractiveMolecule. Only the two-pass `reactantVbGuess` matters.
    //
    // Use zoom `1` for slot + normalized viewBox math only. Live `reactantZoom` (updated
    // from InteractiveMolecule after mount) must NOT recompute these strings — otherwise
    // `useZoomPan` sees a new `calculatedViewBox` and RESETs, wiping PNG-imported
    // viewports (flash correct → snap to default). Arrow/column sizing still uses live
    // zoom via `arrowClusterScale` + ExportableScheme `layoutRef`.
    const zoomForNormalization = 1;
    const buildSlot = (reactantVbGuess: string | undefined) => {
      const cluster = getStableReactionArrowClusterScale(reactantVbGuess, zoomForNormalization);
      const stage = computeReactionStageLayout(
        { width: exportWidth, height: exportHeight },
        topText,
        bottomText,
        arrowType,
        reactantVbGuess,
        zoomForNormalization,
        cluster,
        schemeDisplayZoomBase
      );
      return { slotW: stage.moleculeWidth, slotH: stage.contentHeight };
    };

    try {
      if (reactantConfig && productConfig) {
        let slot = buildSlot(undefined);
        let normalized = computeNormalizedViewBoxes(
          reactantConfig,
          productConfig,
          slot.slotW,
          slot.slotH,
          VIEWBOX_CONFIG.reactantAlign,
          VIEWBOX_CONFIG.productAlign,
          viewBoxScaleMode,
          VIEWBOX_EXTRA_USER_PADDING_UNITS
        );
        slot = buildSlot(normalized.reactantViewBox);
        normalized = computeNormalizedViewBoxes(
          reactantConfig,
          productConfig,
          slot.slotW,
          slot.slotH,
          VIEWBOX_CONFIG.reactantAlign,
          VIEWBOX_CONFIG.productAlign,
          viewBoxScaleMode,
          VIEWBOX_EXTRA_USER_PADDING_UNITS
        );
        return {
          normalizedViewBoxes: normalized,
          reactantIndividualViewBox: null,
          productIndividualViewBox: null,
        };
      }

      if (reactantConfig && !productConfig) {
        let slot = buildSlot(undefined);
        let reactInd = computeSingleViewBox(
          reactantConfig,
          slot.slotW,
          slot.slotH,
          VIEWBOX_CONFIG.reactantAlign,
          viewBoxScaleMode,
          VIEWBOX_EXTRA_USER_PADDING_UNITS
        );
        slot = buildSlot(reactInd);
        reactInd = computeSingleViewBox(
          reactantConfig,
          slot.slotW,
          slot.slotH,
          VIEWBOX_CONFIG.reactantAlign,
          viewBoxScaleMode,
          VIEWBOX_EXTRA_USER_PADDING_UNITS
        );
        return {
          normalizedViewBoxes: null,
          reactantIndividualViewBox: reactInd,
          productIndividualViewBox: null,
        };
      }

      if (productConfig && !reactantConfig) {
        const slot = buildSlot(undefined);
        const prodInd = computeSingleViewBox(
          productConfig,
          slot.slotW,
          slot.slotH,
          VIEWBOX_CONFIG.productAlign,
          viewBoxScaleMode,
          VIEWBOX_EXTRA_USER_PADDING_UNITS
        );
        return {
          normalizedViewBoxes: null,
          reactantIndividualViewBox: null,
          productIndividualViewBox: prodInd,
        };
      }
    } catch {
      /* fall through */
    }

    return {
      normalizedViewBoxes: null,
      reactantIndividualViewBox: null,
      productIndividualViewBox: null,
    };
  }, [
    templateMode,
    reactantConfig,
    productConfig,
    exportWidth,
    exportHeight,
    topText,
    bottomText,
    arrowType,
    viewBoxScaleMode,
    schemeDisplayZoomBase,
    VIEWBOX_EXTRA_USER_PADDING_UNITS,
  ]);

  const moleculeLayoutViewBox = useMemo(() => {
    if (templateMode !== 'molecule' || !reactantConfig) return null;
    const pad = MOLECULE_LAYOUT_BASE.padding;
    const w = Math.max(64, exportWidth - pad * 2);
    const h = Math.max(64, exportHeight - pad * 2);
    const fromBlankBuild =
      buildStarterReactantConfigRef.current !== null &&
      buildStarterReactantConfigRef.current === reactantConfig;
    const horizontalAlign = fromBlankBuild
      ? BUILD_MOLECULE_TEMPLATE_START_ALIGN
      : 'center';
    try {
      return computeSingleViewBox(
        reactantConfig,
        w,
        h,
        horizontalAlign,
        viewBoxScaleMode,
        VIEWBOX_EXTRA_USER_PADDING_UNITS
      );
    } catch {
      return null;
    }
  }, [
    templateMode,
    reactantConfig,
    exportWidth,
    exportHeight,
    viewBoxScaleMode,
    VIEWBOX_EXTRA_USER_PADDING_UNITS,
  ]);

  const reactantLayoutViewBox = useMemo(
    () =>
      normalizedViewBoxes?.reactantViewBox ??
      reactantIndividualViewBox ??
      undefined,
    [normalizedViewBoxes, reactantIndividualViewBox]
  );

  const productLayoutViewBox = useMemo(
    () =>
      normalizedViewBoxes?.productViewBox ??
      productIndividualViewBox ??
      undefined,
    [normalizedViewBoxes, productIndividualViewBox]
  );

  const resolvedReactantViewBox = useMemo(
    () =>
      reactantViewBox ??
      normalizedViewBoxes?.reactantViewBox ??
      reactantIndividualViewBox ??
      undefined,
    [reactantViewBox, normalizedViewBoxes, reactantIndividualViewBox]
  );

  /**
   * Keep row layout/arrow sizing stable while the user pinch-zooms molecules.
   * Scheme zoom (font+percent) still scales the row via `schemeDisplayZoom`,
   * but live molecule pan/zoom should stay inside the SVG viewport only.
   */
  const reactionArrowClusterScale = useMemo(
    () => getStableReactionArrowClusterScale(resolvedReactantViewBox, 1),
    [resolvedReactantViewBox]
  );

  /**
   * Ratio of current slot fit scale vs default export size (reaction only).
   * Shown in the sidebar as displayed zoom % so it tracks apparent size from layout;
   * base `schemeZoomPercent` stays the committed user value.
   */
  const schemeZoomUiToBaseRatio = useMemo(() => {
    if (templateMode !== 'reaction' || !resolvedReactantViewBox) return 1;
    const referenceStage = computeReactionStageLayout(
      DEFAULT_REACTION_EXPORT_DIMENSIONS,
      topText,
      bottomText,
      arrowType,
      resolvedReactantViewBox,
      1,
      reactionArrowClusterScale,
      1
    );
    const currentStage = computeReactionStageLayout(
      { width: exportWidth, height: exportHeight },
      topText,
      bottomText,
      arrowType,
      resolvedReactantViewBox,
      1,
      reactionArrowClusterScale,
      1
    );
    const refScale = getUniformSvgScale(
      resolvedReactantViewBox,
      referenceStage.moleculeWidth,
      referenceStage.contentHeight
    );
    const curScale = getUniformSvgScale(
      resolvedReactantViewBox,
      currentStage.moleculeWidth,
      currentStage.contentHeight
    );
    if (!Number.isFinite(refScale) || !Number.isFinite(curScale) || refScale <= 1e-9 || curScale <= 1e-9) {
      return 1;
    }
    return curScale / refScale;
  }, [
    templateMode,
    resolvedReactantViewBox,
    topText,
    bottomText,
    arrowType,
    reactionArrowClusterScale,
    exportWidth,
    exportHeight,
  ]);

  useEffect(() => {
    schemeZoomUiToBaseRatioRef.current = schemeZoomUiToBaseRatio;
  }, [schemeZoomUiToBaseRatio]);

  const displayedSchemeZoomPercent = useMemo(() => {
    const ratio = templateMode === 'reaction' ? schemeZoomUiToBaseRatio : 1;
    return clampSchemeZoomPercent(
      schemeFontSize,
      Math.round(schemeZoomPercent * ratio)
    );
  }, [templateMode, schemeZoomUiToBaseRatio, schemeFontSize, schemeZoomPercent]);

  /** Rendering zoom follows displayed % so arrow/text track apparent molecule scale. */
  const schemeDisplayZoom = useMemo(
    () => computeEffectiveSchemeDisplayZoom(schemeFontSize, displayedSchemeZoomPercent),
    [schemeFontSize, displayedSchemeZoomPercent]
  );

  const handleDisplayedSchemeZoomPercentChange = useCallback(
    (displayPercent: number) => {
      const ratio = Math.max(1e-9, schemeZoomUiToBaseRatioRef.current);
      const basePercent = Math.round(displayPercent / ratio);
      handleSchemeZoomPercentChange(basePercent);
    },
    [handleSchemeZoomPercentChange]
  );

  const handleDisplayedSchemeZoomSettled = useCallback(
    (
      percentCommitted: number,
      _before: { percent: number }
    ) => {
      const ratio = Math.max(1e-9, schemeZoomUiToBaseRatioRef.current);
      const basePercent = Math.round(percentCommitted / ratio);
      handleSchemeZoomSettled(basePercent, { percent: schemeZoomPercentRef.current });
    },
    [handleSchemeZoomSettled]
  );

  /** Effective `<text>` anchor for overlay labels (import + Add Text defaults + legacy fallbacks). */
  const reactantOverlayAlignResolved = useMemo((): OverlayTextHorizontalAlign => {
    if (!reactantOverlayText) return 'start';
    if (reactantOverlayTextAlign != null) return reactantOverlayTextAlign;
    if (templateMode === 'molecule') {
      return reactantOverlayPosition ? 'start' : 'middle';
    }
    return reactantOverlayPosition ? 'start' : 'end';
  }, [reactantOverlayText, reactantOverlayTextAlign, templateMode, reactantOverlayPosition]);

  const productOverlayAlignResolved = useMemo((): OverlayTextHorizontalAlign => {
    if (!productOverlayText) return 'start';
    return productOverlayTextAlign ?? 'start';
  }, [productOverlayText, productOverlayTextAlign]);

  // Export handling (PNG/SVG with embedded metadata) — after reactionArrowClusterScale
  const schemeExport = useSchemeExport(
    {
      templateMode,
      reactantConfig,
      productConfig,
      topText,
      bottomText,
      arrowType,
      altText,
      reactantOverlayText,
      productOverlayText,
      reactantOverlayPosition,
      productOverlayPosition,
      reactantOverlayTextAlign: reactantOverlayText ? reactantOverlayAlignResolved : undefined,
      productOverlayTextAlign: productOverlayText ? productOverlayAlignResolved : undefined,
      exportWidth,
      exportHeight,
      reactantZoom: reactionArrowClusterScale,
      schemeDisplayZoom,
      schemeFontSize,
      schemeZoomPercent,
      reactantViewBox,
      productViewBox,
    },
    exportRef,
  );

  const executeExport = useCallback(async (filename: string, format: 'svg' | 'png'): Promise<boolean> => {
    try {
      if (format === 'svg') {
        return await schemeExport.handleDownloadSvg(filename);
      }
      return await schemeExport.handleDownloadPng(filename);
    } catch (e) {
      console.error('[ReactionScheme] Export failed', e);
      return false;
    }
  }, [schemeExport]);

  const handleExport = useCallback(
    async (filename: string, format: 'svg' | 'png') => {
      if (!bypassBilling && authReady) {
        const ls = licenseState.status;
        if (ls === 'revoked') {
          showToastMessage(
            'Your Piph Author license is no longer active. Renew or subscribe to export.',
          );
          routeToUpgradeForAction('export');
          return;
        }
        if (ls === 'expired') {
          showToastMessage(
            'Your Piph Author license is no longer active. Renew or subscribe to export.',
          );
          routeToUpgradeBuy();
          return;
        }
        if (ls === 'error') {
          showToastMessage(
            "We couldn't verify your license. Check your connection and try again, or open Upgrade and use Refresh license.",
          );
          return;
        }
      }
      if (!bypassBilling && !isAuthenticated()) {
        if (!trialUsage.trialAllowsAction) {
          showToastMessage(
            'You have used every action included in the free trial. Subscribe or redeem a license to export.',
          );
          routeToUpgradeForAction('export');
          return;
        }
      }
      if (skipMissingAltTextConfirm || altText.trim().length > 0) {
        const ok = await executeExport(filename, format);
        if (ok && !bypassBilling && !isAuthenticated()) {
          const left = await trialUsage.consumeTrialUse();
          if (left !== null) {
            showTrialConsumptionToast(left);
          }
        }
        return;
      }
      setDontAskExportWithoutAltAgain(false);
      setPendingExportRequest({ filename, format });
    },
    [
      altText,
      authReady,
      bypassBilling,
      licenseState.status,
      skipMissingAltTextConfirm,
      executeExport,
      routeToUpgradeBuy,
      routeToUpgradeForAction,
      showToastMessage,
      showTrialConsumptionToast,
      trialUsage,
    ],
  );

  const cancelExportWithoutAltText = useCallback(() => {
    setPendingExportRequest(null);
    setDontAskExportWithoutAltAgain(false);
  }, []);

  const confirmExportWithoutAltText = useCallback(async () => {
    if (!pendingExportRequest) return;

    if (!bypassBilling && authReady) {
      const ls = licenseState.status;
      if (ls === 'revoked') {
        showToastMessage(
          'Your Piph Author license is no longer active. Renew or subscribe to export.',
        );
        setPendingExportRequest(null);
        routeToUpgradeForAction('export');
        return;
      }
      if (ls === 'expired') {
        showToastMessage(
          'Your Piph Author license is no longer active. Renew or subscribe to export.',
        );
        setPendingExportRequest(null);
        routeToUpgradeBuy();
        return;
      }
      if (ls === 'error') {
        showToastMessage(
          "We couldn't verify your license. Check your connection and try again, or open Upgrade and use Refresh license.",
        );
        setPendingExportRequest(null);
        return;
      }
    }
    if (!bypassBilling && !isAuthenticated()) {
      if (!trialUsage.trialAllowsAction) {
        showToastMessage(
          'You have used every action included in the free trial. Subscribe or redeem a license to export.',
        );
        setPendingExportRequest(null);
        routeToUpgradeForAction('export');
        return;
      }
    }

    if (dontAskExportWithoutAltAgain) {
      try {
        localStorage.setItem(EXPORT_WITHOUT_ALT_CONFIRM_KEY, '1');
      } catch {
        /* ignore persistence failures */
      }
      setSkipMissingAltTextConfirm(true);
    }
    const { filename, format } = pendingExportRequest;
    setPendingExportRequest(null);
    setDontAskExportWithoutAltAgain(false);
    const ok = await executeExport(filename, format);
    if (ok && !bypassBilling && !isAuthenticated()) {
      const left = await trialUsage.consumeTrialUse();
      if (left !== null) {
        showTrialConsumptionToast(left);
      }
    }
  }, [
    pendingExportRequest,
    authReady,
    bypassBilling,
    dontAskExportWithoutAltAgain,
    executeExport,
    licenseState.status,
    routeToUpgradeBuy,
    routeToUpgradeForAction,
    showToastMessage,
    showTrialConsumptionToast,
    trialUsage,
  ]);

  // Compute JSON from current state based on template mode
  const currentJson = useMemo(() => {
    if (templateMode === 'molecule') {
      const config: MoleculeConfig = {
        config: reactantConfig,
      };
      return JSON.stringify(config, null, 2);
    } else {
      const config: ReactionConfig = {
        reactant: reactantConfig,
        product: productConfig,
        reagents: topText,
        conditions: bottomText,
        arrowType: arrowType,
      };
      // Only include viewbox values if user has made custom zoom/pan adjustments
    if (reactantViewBox) {
      config.reactantViewBox = reactantViewBox;
    }
    if (productViewBox) {
      config.productViewBox = productViewBox;
    }
    return JSON.stringify(config, null, 2);
    }
  }, [templateMode, reactantConfig, productConfig, topText, bottomText, arrowType, reactantViewBox, productViewBox]);

  // Update JSON input when state changes
  useEffect(() => {
    setJsonInput(currentJson);
  }, [currentJson]);

  const exportPreviewNode = templateMode === 'molecule' ? (
    <SingleMoleculeExport
      ref={exportRef}
      config={reactantConfig}
      viewBox={reactantViewBox ?? moleculeLayoutViewBox ?? undefined}
      layoutViewBox={moleculeLayoutViewBox ?? undefined}
      persistedViewBox={reactantViewBox}
      overlayText={reactantOverlayText}
      overlayTextHorizontalAlign={reactantOverlayAlignResolved}
      exportWidth={exportWidth}
      exportHeight={exportHeight}
      schemeDisplayZoom={schemeDisplayZoom}
    />
  ) : (
    <ExportableScheme
      ref={exportRef}
      reactantConfig={reactantConfig}
      productConfig={productConfig}
      topText={topText}
      bottomText={bottomText}
      arrowType={arrowType}
      reactantViewBox={resolvedReactantViewBox}
      productViewBox={productViewBox ?? normalizedViewBoxes?.productViewBox ?? productIndividualViewBox ?? undefined}
      reactantLayoutViewBox={reactantLayoutViewBox}
      productLayoutViewBox={productLayoutViewBox}
      reactantPersistedViewBox={reactantViewBox}
      productPersistedViewBox={productViewBox}
      reactantOverlayText={reactantOverlayText}
      productOverlayText={productOverlayText}
      reactantOverlayTextAlign={reactantOverlayAlignResolved}
      productOverlayTextAlign={productOverlayAlignResolved}
      reactantZoom={1}
      arrowClusterScale={reactionArrowClusterScale}
      schemeDisplayZoom={schemeDisplayZoom}
      exportWidth={exportWidth}
      exportHeight={exportHeight}
    />
  );

  const handleDragOver = useCallback((_target: 'reactant' | 'product' | 'molecule') => (e: DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDrop = useCallback((target: 'reactant' | 'product' | 'molecule') => async (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const importer = target === 'reactant' ? reactantImport : target === 'product' ? productImport : moleculeImport;
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      await importer.processFile(files[0]);
      return;
    }
    const text = e.dataTransfer.getData('text/plain');
    if (text && importer.isCDXML(text)) {
      importer.processCDXML(text);
    }
  }, [moleculeImport, productImport, reactantImport]);

  const handlePaste = useCallback((target: 'reactant' | 'product' | 'molecule') => (e: ClipboardEvent<HTMLDivElement>) => {
    const importer = target === 'reactant' ? reactantImport : target === 'product' ? productImport : moleculeImport;
    const text = e.clipboardData.getData('text/plain');
    if (text && importer.isCDXML(text)) {
      e.preventDefault();
      importer.processCDXML(text);
    }
  }, [moleculeImport, productImport, reactantImport]);

  const overlayPositionPropsMolecule = {
    overlayPosition: reactantOverlayPosition,
    onOverlayPositionChange: setReactantOverlayPosition,
    overlayTextHorizontalAlign: reactantOverlayAlignResolved,
  };

  const overlayPositionPropsReaction = {
    reactantOverlayPosition,
    productOverlayPosition,
    onReactantOverlayPositionChange: setReactantOverlayPosition,
    onProductOverlayPositionChange: setProductOverlayPosition,
    reactantOverlayTextAlign: reactantOverlayAlignResolved,
    productOverlayTextAlign: productOverlayAlignResolved,
  };

  const previewWithDropZones = isValidElement(exportPreviewNode)
    ? cloneElement(exportPreviewNode, {
        id: 'build-preview-export',
        className:
          templateMode === 'molecule'
            ? 'build-preview-export build-preview-export--molecule'
            : 'build-preview-export build-preview-export--reaction',
        ...(templateMode === 'molecule' ? overlayPositionPropsMolecule : overlayPositionPropsReaction),
        showMoleculeDropTarget: templateMode === 'molecule' && !reactantConfig && !reactantOverlayText,
        showReactantDropTarget: templateMode === 'reaction' && !reactantConfig && !reactantOverlayText,
        showProductDropTarget: templateMode === 'reaction' && !productConfig && !productOverlayText,
        onMoleculeDragOver: handleDragOver('molecule'),
        onMoleculeDrop: handleDrop('molecule'),
        onReactantDragOver: handleDragOver('reactant'),
        onReactantDrop: handleDrop('reactant'),
        onProductDragOver: handleDragOver('product'),
        onProductDrop: handleDrop('product'),
        onMoleculePaste: handlePaste('molecule'),
        onReactantPaste: handlePaste('reactant'),
        onProductPaste: handlePaste('product'),
        selectedMolecule: selectedPasteTarget === 'molecule',
        selectedReactant: selectedPasteTarget === 'reactant',
        selectedProduct: selectedPasteTarget === 'product',
        selectedArrow: selectedPasteTarget === 'arrow',
        onMoleculeSelect: () => setSelectedPasteTarget('molecule'),
        onReactantSelect: () => setSelectedPasteTarget('reactant'),
        onProductSelect: () => setSelectedPasteTarget('product'),
        onReactantDoubleClick: () => {
          setSelectedPasteTarget('reactant');
          if (reactantConfig) {
            sketcherModal.editReactantExisting();
          } else {
            sketcherModal.editReactant();
          }
        },
        onProductDoubleClick: () => {
          setSelectedPasteTarget('product');
          if (productConfig) {
            sketcherModal.editProductExisting();
          } else {
            sketcherModal.editProduct();
          }
        },
        onArrowSelect: () => setSelectedPasteTarget('arrow'),
        interactivePreview: true,
        onReactantViewBoxChange: setReactantViewBox,
        onProductViewBoxChange: setProductViewBox,
        onMoleculeViewBoxChange: setReactantViewBox,
      })
    : exportPreviewNode;

  const basePreviewScale = previewFitScale * PREVIEW_DISPLAY_SCALE;
  const canvasScale = Math.min(6, Math.max(0.2, basePreviewScale * previewZoom));
  const scaledWidth = exportWidth * canvasScale;
  const scaledHeight = exportHeight * canvasScale;

  const refreshMoleculeSlotBounds = useCallback(() => {
    if (!showMoleculeBounds || templateMode !== 'reaction' || !exportRef.current) {
      setMoleculeSlotBounds(null);
      return;
    }
    setMoleculeSlotBounds(measureReactionMoleculeBoundsInExportSpace(exportRef.current));
  }, [showMoleculeBounds, templateMode]);


  const handleToggleMoleculeBounds = useCallback(() => {
    setShowMoleculeBounds((v) => !v);
  }, []);

  useLayoutEffect(() => {
    if (!showMoleculeBounds || templateMode !== 'reaction') {
      setMoleculeSlotBounds(null);
      return;
    }
    const rafId = requestAnimationFrame(() => refreshMoleculeSlotBounds());
    return () => cancelAnimationFrame(rafId);
  }, [
    showMoleculeBounds,
    templateMode,
    refreshMoleculeSlotBounds,
    exportWidth,
    exportHeight,
    canvasScale,
    previewZoom,
    previewFitScale,
    reactantConfig,
    productConfig,
    topText,
    bottomText,
    arrowType,
    schemeDisplayZoom,
    resolvedReactantViewBox,
    reactantLayoutViewBox,
    productLayoutViewBox,
    normalizedViewBoxes,
    reactantViewBox,
    productViewBox,
    reactantOverlayText,
    productOverlayText,
    reactionArrowClusterScale,
  ]);


  useEffect(() => {
    if (!showMoleculeBounds || templateMode !== 'reaction') return;
    const el = exportRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(() => refreshMoleculeSlotBounds());
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [showMoleculeBounds, templateMode, refreshMoleculeSlotBounds]);

  const refreshCompactArrowDock = useCallback(() => {
    if (!compactArrowViewport || templateMode !== 'reaction' || selectedPasteTarget !== 'arrow') {
      setCompactArrowDockCenterX(null);
      return;
    }
    const mount = exportRef.current;
    const frameRaw = mount?.closest('.build-preview-export-frame');
    const frameEl = frameRaw instanceof HTMLElement ? frameRaw : null;
    if (!mount || !frameEl) {
      setCompactArrowDockCenterX(null);
      return;
    }
    const dock = computePreviewArrowDockCenterX(frameEl, mount, scaledWidth, {
      panelWidth: ARROW_CONTROLS_COMPACT_PANEL_WIDTH_PX,
      gap: 10,
    });
    setCompactArrowDockCenterX(dock);
  }, [
    compactArrowViewport,
    templateMode,
    selectedPasteTarget,
    scaledWidth,
  ]);

  const scheduleCompactArrowDock = useCallback(() => {
    if (arrowCompactDockRafRef.current !== null) return;
    arrowCompactDockRafRef.current = requestAnimationFrame(() => {
      arrowCompactDockRafRef.current = null;
      refreshCompactArrowDock();
    });
  }, [refreshCompactArrowDock]);

  useLayoutEffect(() => {
    scheduleCompactArrowDock();
  }, [
    scheduleCompactArrowDock,
    templateMode,
    selectedPasteTarget,
    compactArrowViewport,
    exportWidth,
    exportHeight,
    canvasScale,
    previewZoom,
    previewFitScale,
    reactantConfig,
    productConfig,
    topText,
    bottomText,
    arrowType,
    schemeDisplayZoom,
    resolvedReactantViewBox,
    reactantLayoutViewBox,
    productLayoutViewBox,
    normalizedViewBoxes,
    reactantViewBox,
    productViewBox,
    reactantOverlayText,
    productOverlayText,
    reactionArrowClusterScale,
  ]);

  useEffect(() => {
    if (!compactArrowViewport || templateMode !== 'reaction' || selectedPasteTarget !== 'arrow') {
      return;
    }
    const mount = exportRef.current;
    if (!mount || typeof ResizeObserver === 'undefined') return;
    const frameRaw = mount.closest('.build-preview-export-frame');
    const frameEl = frameRaw instanceof HTMLElement ? frameRaw : null;
    if (!frameEl) return;
    const ro = new ResizeObserver(scheduleCompactArrowDock);
    ro.observe(frameEl);
    ro.observe(mount);
    return () => {
      ro.disconnect();
      if (arrowCompactDockRafRef.current !== null) {
        cancelAnimationFrame(arrowCompactDockRafRef.current);
        arrowCompactDockRafRef.current = null;
      }
    };
  }, [
    compactArrowViewport,
    templateMode,
    selectedPasteTarget,
    scheduleCompactArrowDock,
  ]);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTypingContext =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.tagName === 'SELECT' ||
        Boolean(target?.isContentEditable);
      if (isTypingContext) return;
      if (!event.altKey) return;
      const key = event.key.toLowerCase();
      if (key === 'r') {
        event.preventDefault();
        if (templateMode === 'reaction') {
          if (reactantConfig) {
            sketcherModal.editReactantExisting();
          } else {
            sketcherModal.editReactant();
          }
        } else {
          if (reactantConfig) {
            sketcherModal.editMoleculeExisting();
          } else {
            sketcherModal.editMolecule();
          }
        }
      } else if (key === 'p' && templateMode === 'reaction') {
        event.preventDefault();
        if (productConfig) {
          sketcherModal.editProductExisting();
        } else {
          sketcherModal.editProduct();
        }
      } else if (key === 'a' && templateMode === 'reaction') {
        event.preventDefault();
        reagentsInputRef.current?.focus();
        reagentsInputRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    };
    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, [templateMode, reactantConfig, productConfig, sketcherModal]);

  useEffect(() => {
    const viewport = previewViewportRef.current;
    if (!viewport) {
      setPreviewFitScale(1);
      return;
    }
    const updateScale = () => {
      const bounds = viewport.getBoundingClientRect();
      const widthScale = bounds.width / exportWidth;
      const heightScale = bounds.height / exportHeight;
      const nextScale = Math.min(widthScale, heightScale, 1);
      setPreviewFitScale(Number.isFinite(nextScale) ? nextScale : 1);
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [exportWidth, exportHeight]);

  useEffect(() => {
    setPreviewZoom(1);
    previewZoomRef.current = 1;
    const viewport = previewViewportRef.current;
    if (viewport) {
      viewport.scrollLeft = 0;
      viewport.scrollTop = 0;
    }
  }, [exportWidth, exportHeight, templateMode]);

  const commitZoom = useCallback((nextZoom: number) => {
    pendingZoomRef.current = Math.min(6, Math.max(0.2, nextZoom));
    if (zoomRafRef.current !== null) return;
    zoomRafRef.current = requestAnimationFrame(() => {
      zoomRafRef.current = null;
      if (pendingZoomRef.current == null) return;
      previewZoomRef.current = pendingZoomRef.current;
      setPreviewZoom(pendingZoomRef.current);
      pendingZoomRef.current = null;
    });
  }, []);

  const zoomAtPoint = useCallback((clientX: number, clientY: number, nextZoomRaw: number) => {
    const viewport = previewViewportRef.current;
    if (!viewport) return;
    const nextZoom = Math.min(6, Math.max(0.2, nextZoomRaw));
    const prevScale = basePreviewScale * previewZoomRef.current;
    const nextScale = basePreviewScale * nextZoom;
    if (prevScale <= 0 || nextScale <= 0) return;

    const rect = viewport.getBoundingClientRect();
    const xInViewport = clientX - rect.left;
    const yInViewport = clientY - rect.top;

    // Keep the gesture focal point stable while zooming.
    const worldX = (viewport.scrollLeft + xInViewport) / prevScale;
    const worldY = (viewport.scrollTop + yInViewport) / prevScale;

    commitZoom(nextZoom);

    requestAnimationFrame(() => {
      viewport.scrollLeft = worldX * nextScale - xInViewport;
      viewport.scrollTop = worldY * nextScale - yInViewport;
    });
  }, [basePreviewScale, commitZoom]);

  const handlePreviewPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('svg')) return;
    // Paste/select slots rely on click — viewport pointer capture retargets subsequent
    // events and prevents click from firing on reactant/product/arrow (non-SVG) areas.
    if (target.closest('.preview-paste-slot')) return;
    // Do not capture pointers for preview chrome (buttons, dropdowns) or the inline
    // control row — capture would swallow the click so controls never open.
    if (target.closest('.preview-inline-controls')) return;
    if (target.closest('.preview-arrow-panel')) return;
    if (target.closest('button, a, input, select, textarea')) return;
    if (!previewViewportRef.current) return;
    (event.currentTarget as HTMLDivElement).setPointerCapture(event.pointerId);
    pointerStartRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointerStartRef.current.size === 2) {
      const [a, b] = Array.from(pointerStartRef.current.values());
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      pinchStartRef.current = { distance, zoom: previewZoomRef.current };
    }
  }, []);

  const handlePreviewPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const viewport = previewViewportRef.current;
    if (!viewport || !pointerStartRef.current.has(event.pointerId)) return;
    pointerStartRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointerStartRef.current.size === 2 && pinchStartRef.current) {
      const [a, b] = Array.from(pointerStartRef.current.values());
      const nextDistance = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchStartRef.current.distance > 0) {
        const ratio = nextDistance / pinchStartRef.current.distance;
        const scaledRatio = Math.pow(ratio, PREVIEW_ZOOM_SENSITIVITY);
        const nextZoom = pinchStartRef.current.zoom * scaledRatio;
        const midX = (a.x + b.x) / 2;
        const midY = (a.y + b.y) / 2;
        zoomAtPoint(midX, midY, nextZoom);
      }
      return;
    }

  }, [zoomAtPoint]);

  const handlePreviewPointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    pointerStartRef.current.delete(event.pointerId);
    if (pointerStartRef.current.size < 2) {
      pinchStartRef.current = null;
    }
  }, []);

  const clampSidePanelWidth = useCallback(
    (next: number) => Math.min(SIDE_PANEL_WIDTH_MAX, Math.max(SIDE_PANEL_WIDTH_MIN, Math.round(next))),
    []
  );

  const handleSidePanelResizeStart = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const el = event.currentTarget;
    el.setPointerCapture(event.pointerId);
    sidePanelResizeRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: sidePanelWidth,
      el,
    };
  }, [sidePanelWidth]);

  const handleSidePanelResizeMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const active = sidePanelResizeRef.current;
    if (!active || active.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - active.startX;
    setSidePanelWidth(clampSidePanelWidth(active.startWidth + deltaX));
  }, [clampSidePanelWidth]);

  const handleSidePanelResizeEnd = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const active = sidePanelResizeRef.current;
    if (!active || active.pointerId !== event.pointerId) return;
    try {
      active.el.releasePointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
    sidePanelResizeRef.current = null;
  }, []);

  useEffect(() => {
    const viewport = previewViewportRef.current;
    if (!viewport) return;

    const preventGestureZoom = (e: Event) => e.preventDefault();

    // Must be non-passive: React's onWheel is passive, so preventDefault would warn and pinch-zoom
    // (trackpad wheel + ctrlKey) could not cancel browser zoom consistently.
    const handlePreviewWheelCapture = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const factor = Math.exp(-e.deltaY * 0.004 * PREVIEW_ZOOM_SENSITIVITY);
      zoomAtPoint(e.clientX, e.clientY, previewZoomRef.current * factor);
    };

    viewport.addEventListener('gesturestart', preventGestureZoom, { passive: false } as AddEventListenerOptions);
    viewport.addEventListener('gesturechange', preventGestureZoom, { passive: false } as AddEventListenerOptions);
    viewport.addEventListener('gestureend', preventGestureZoom, { passive: false } as AddEventListenerOptions);
    viewport.addEventListener('wheel', handlePreviewWheelCapture, { passive: false });
    return () => {
      viewport.removeEventListener('gesturestart', preventGestureZoom);
      viewport.removeEventListener('gesturechange', preventGestureZoom);
      viewport.removeEventListener('gestureend', preventGestureZoom);
      viewport.removeEventListener('wheel', handlePreviewWheelCapture);
      if (zoomRafRef.current !== null) {
        cancelAnimationFrame(zoomRafRef.current);
        zoomRafRef.current = null;
      }
    };
  }, [zoomAtPoint]);

  const selectedReactionControl = useMemo(() => {
    if (templateMode !== 'reaction') return null;
    if (selectedPasteTarget === 'reactant') {
      return (
        <div className="preview-selected-controls-bar__content preview-selected-controls-bar__content--reactant">
          <MoleculeEditToolbar
            label="Reactant"
            onCreateSVG={sketcherModal.editReactant}
            onAddText={handleAddReactantText}
            afterAddText={
              <button
                type="button"
                onClick={moleculeTransfer.transferProductToReactant}
                className="transfer-button"
                aria-label="Transfer product to reactant"
                title="Copy from product"
              >
                <ClipboardIcon />
              </button>
            }
            configString={reactantConfig}
            onRemoveConfig={handleRemoveReactantConfig}
            onEditExisting={sketcherModal.editReactantExisting}
            overlayText={reactantOverlayText}
            onRemoveText={handleRemoveReactantText}
          />
        </div>
      );
    }
    if (selectedPasteTarget === 'product') {
      return (
        <div className="preview-selected-controls-bar__content preview-selected-controls-bar__content--product">
          <MoleculeEditToolbar
            label="Product"
            onCreateSVG={sketcherModal.editProduct}
            onAddText={handleAddProductText}
            afterAddText={
              <button
                type="button"
                onClick={moleculeTransfer.transferReactantToProduct}
                className="transfer-button"
                aria-label="Transfer reactant to product"
                title="Copy from reactant"
              >
                <ClipboardIcon />
              </button>
            }
            configString={productConfig}
            onRemoveConfig={handleRemoveProductConfig}
            onEditExisting={sketcherModal.editProductExisting}
            overlayText={productOverlayText}
            onRemoveText={handleRemoveProductText}
          />
        </div>
      );
    }
    if (selectedPasteTarget === 'arrow') {
      const compactSideDocked =
        compactArrowViewport &&
        compactArrowDockCenterX != null;
      const arrowStripStyle: CSSProperties | undefined = compactSideDocked
        ? {
            position: 'absolute',
            left: compactArrowDockCenterX,
            top: 0,
            transform: 'translateX(-50%)',
            marginLeft: 0,
          }
        : undefined;
      return (
        <div
          className="preview-selected-controls-bar__content preview-selected-controls-bar__content--arrow"
          style={arrowStripStyle}
        >
          <PreviewArrowPanel
            topText={topText}
            bottomText={bottomText}
            arrowType={arrowType}
            onTopTextChange={handleTopTextChange}
            onBottomTextChange={handleBottomTextChange}
            onArrowTypeChange={handleArrowTypeChange}
            reagentsInputRef={reagentsInputRef}
            conditionsInputRef={conditionsInputRef}
            characterPalette={characterPalette}
          />
        </div>
      );
    }
    return null;
  }, [
    templateMode,
    selectedPasteTarget,
    moleculeTransfer,
    sketcherModal,
    handleAddReactantText,
    reactantConfig,
    handleRemoveReactantConfig,
    reactantOverlayText,
    handleRemoveReactantText,
    handleAddProductText,
    productConfig,
    handleRemoveProductConfig,
    productOverlayText,
    handleRemoveProductText,
    topText,
    bottomText,
    arrowType,
    handleTopTextChange,
    handleBottomTextChange,
    handleArrowTypeChange,
    characterPalette,
    compactArrowViewport,
    compactArrowDockCenterX,
  ]);

  const selectedMoleculeControl = useMemo(() => {
    if (templateMode !== 'molecule' || selectedPasteTarget !== 'molecule') return null;
    return (
      <div className="preview-selected-controls-bar__content preview-selected-controls-bar__content--molecule">
        <MoleculeEditToolbar
          label="Molecule"
          onCreateSVG={sketcherModal.editMolecule}
          onAddText={handleAddReactantText}
          configString={reactantConfig}
          onRemoveConfig={handleRemoveReactantConfig}
          onEditExisting={sketcherModal.editMoleculeExisting}
          overlayText={reactantOverlayText}
          onRemoveText={handleRemoveReactantText}
        />
      </div>
    );
  }, [
    templateMode,
    selectedPasteTarget,
    sketcherModal,
    handleAddReactantText,
    reactantConfig,
    handleRemoveReactantConfig,
    reactantOverlayText,
    handleRemoveReactantText,
  ]);

  const selectedPreviewControls = selectedReactionControl ?? selectedMoleculeControl;

  return (
    <>
      <div
        className={`figma-app-with-trial h-full min-h-0 w-full${showLicenseStatusBanner ? ' figma-app-with-trial--license-banner' : ''}`}
      >
        {showLicenseStatusBanner && (
          <LicenseStatusBanner
            status={licenseState.status}
            trialRemaining={trialUsage.remaining}
            trialLimit={trialUsage.trialLimit}
            billingConfigured={billingConfigured}
            onSubscribe={routeToUpgradeBuy}
            onManageBilling={() => void openBillingPortal()}
          />
        )}
        <div
          className="figma-shell-layout"
          style={{ '--figma-side-panel-width': `${sidePanelWidth}px` } as CSSProperties}
        >
        <aside className="figma-side-panel">
          <BuildSection
            templateMode={templateMode}
            onTemplateChange={handleTemplateChange}
            reactantConfig={reactantConfig}
            productConfig={productConfig}
            onReactantConfigChange={handleReactantConfigChange}
            onProductConfigChange={handleProductConfigChange}
            reactantOverlayText={reactantOverlayText}
            productOverlayText={productOverlayText}
            onAddReactantText={handleAddReactantText}
            onAddProductText={handleAddProductText}
            onRemoveReactantConfig={handleRemoveReactantConfig}
            onRemoveProductConfig={handleRemoveProductConfig}
            onRemoveReactantText={handleRemoveReactantText}
            onRemoveProductText={handleRemoveProductText}
            moleculeTransfer={moleculeTransfer}
            sketcherModal={sketcherModal}
            onAlchemieImport={schemeImport.handleAlchemieImport}
            onImportError={(msg) => { showToastMessage(`Import failed: ${msg}`); }}
            onResetBuild={handleResetBuildRequest}
            exportWidth={exportWidth}
            exportHeight={exportHeight}
            aspectLock={lockAspectRatio}
            onExportWidthChange={handleExportWidthChange}
            onExportHeightChange={handleExportHeightChange}
            onAspectLockChange={setLockAspectRatio}
            schemeZoomPercent={displayedSchemeZoomPercent}
            onSchemeZoomPercentChange={handleDisplayedSchemeZoomPercentChange}
            onSchemeZoomSettled={handleDisplayedSchemeZoomSettled}
            showMoleculeBounds={showMoleculeBounds}
            onToggleMoleculeBounds={handleToggleMoleculeBounds}
          />

          <ExportSection
            templateMode={templateMode}
            isDevMode={isDevMode}
            altText={altText}
            systemPrompt={systemPrompt}
            contextPrompt={contextPrompt}
            selectedContextOption={selectedContextOption}
            onAltTextChange={setAltText}
            onSystemPromptChange={setSystemPrompt}
            onContextPromptChange={setContextPrompt}
            onContextOptionSelect={setSelectedContextOption}
            altTextGeneration={{
              ...altTextGeneration,
              generateAltText: handleGenerateAltText,
            }}
            onExport={handleExport}
          />
        </aside>
        <div
          className="figma-side-panel-resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize side panel"
          onPointerDown={handleSidePanelResizeStart}
          onPointerMove={handleSidePanelResizeMove}
          onPointerUp={handleSidePanelResizeEnd}
          onPointerCancel={handleSidePanelResizeEnd}
        />

        <section className="figma-preview-center">
          <div className="build-live-preview" id="build-live-preview" data-build-template={templateMode}>
            <p id="build-preview-export-bounds-hint" className="build-preview-bounds-hint">
              Select to edit. Drag molecules to move. Pinch or Ctrl/Cmd + wheel to zoom.
            </p>
            <div
              ref={previewViewportRef}
              className="build-preview-viewport figma-preview-viewport"
              id="build-preview-viewport"
              aria-describedby="build-preview-export-bounds-hint"
              onPointerDown={handlePreviewPointerDown}
              onPointerMove={handlePreviewPointerMove}
              onPointerUp={handlePreviewPointerUp}
              onPointerCancel={handlePreviewPointerUp}
            >
              <div className="build-preview-content" id="build-preview-content" style={{ minWidth: scaledWidth, minHeight: scaledHeight }}>
                <div className="build-preview-frame-stack">
                  {selectedPreviewControls && (
                    <div
                      className="preview-selected-controls-bar"
                      aria-label={
                        templateMode === 'molecule'
                          ? 'Selected molecule controls'
                          : 'Selected reaction controls'
                      }
                    >
                      {selectedPreviewControls}
                    </div>
                  )}
                  <div className="build-preview-export-frame" style={{ width: scaledWidth, height: scaledHeight, display: 'flex', alignItems: 'stretch', justifyContent: 'stretch' }}>
                    <div
                      className="build-preview-export-scale-wrap"
                      style={{
                        position: 'relative',
                        transform: `scale(${canvasScale})`,
                        transformOrigin: 'top left',
                        width: exportWidth,
                        height: exportHeight,
                      }}
                    >
                      {previewWithDropZones}
                      {templateMode === 'reaction' && showMoleculeBounds && moleculeSlotBounds && (
                        <div className="molecule-bounds-overlay" aria-hidden>
                          {moleculeSlotBounds.reactant && (
                            <div
                              className="molecule-bounds-overlay__rect molecule-bounds-overlay__rect--reactant"
                              style={{
                                left: moleculeSlotBounds.reactant.x,
                                top: moleculeSlotBounds.reactant.y,
                                width: moleculeSlotBounds.reactant.width,
                                height: moleculeSlotBounds.reactant.height,
                              }}
                            />
                          )}
                          {moleculeSlotBounds.product && (
                            <div
                              className="molecule-bounds-overlay__rect molecule-bounds-overlay__rect--product"
                              style={{
                                left: moleculeSlotBounds.product.x,
                                top: moleculeSlotBounds.product.y,
                                width: moleculeSlotBounds.product.width,
                                height: moleculeSlotBounds.product.height,
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Right panel: Structure Tree for molecule navigation */}
        <StructureTreePanel
          reactantConfig={reactantConfig}
          productConfig={productConfig}
          templateMode={templateMode}
          arrowType={arrowType}
          topText={topText}
          bottomText={bottomText}
        />
        </div>
      </div>

      {/* Sketcher Overlay for editing molecules */}
      <SketcherOverlay
        open={sketcherModal.open}
        initialConfig={sketcherModal.initialConfig}
        label={sketcherModal.label}
        onSave={handleSketcherSave}
        onClose={sketcherModal.close}
      />

      {/* Text Input Dialog for adding text overlays */}
      <TextInputDialog
        open={textDialogOpen}
        title={`Add Text to ${textDialogTarget === 'reactant' ? 'Reactant' : 'Product'}`}
        initialValue={textDialogTarget === 'reactant' ? reactantOverlayText : productOverlayText}
        placeholder="Enter text to display..."
        onSave={handleSaveText}
        onClose={handleCloseTextDialog}
        context={textDialogTarget}
      />

      {/* ARIA live region for screen readers */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {characterPalette.copiedCharacter && `Character ${characterPalette.copiedCharacter} copied to clipboard`}
      </div>

      {/* Import confirmation dialog */}
      <ConfirmDialog
        open={schemeImport.pendingImport !== null}
        title="Replace Current Scheme?"
        message="Importing this file will replace your current reactant, product, and all settings."
        buttons={[
          { label: 'Cancel', onClick: schemeImport.cancelImport },
          { label: 'Replace', onClick: schemeImport.confirmImport, variant: 'primary' },
        ]}
      />

      <ConfirmDialog
        open={pendingTemplateMode !== null}
        title="Switch Template?"
        message="Switching templates will clear your current build data. Do you want to continue?"
        buttons={[
          { label: 'Cancel', onClick: cancelTemplateChange },
          { label: 'Switch', onClick: confirmTemplateChange, variant: 'primary' },
        ]}
      />

      <ConfirmDialog
        open={resetBuildConfirmOpen}
        title="Reset Build?"
        message="This will clear molecules, arrow labels, text overlays, alt text, and reset zoom to 100%. Do you want to continue?"
        checkbox={{
          label: 'Do not ask again in the future',
          checked: dontAskResetBuildAgain,
          onChange: setDontAskResetBuildAgain,
        }}
        buttons={[
          { label: 'Cancel', onClick: cancelResetBuild },
          { label: 'Reset', onClick: confirmResetBuild, variant: 'primary' },
        ]}
      />

      {pendingExportRequest !== null && (
        <div className="confirm-dialog-overlay">
          <div
            className="confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-export-without-alt-title"
            aria-describedby="confirm-export-without-alt-message"
          >
            <h3 id="confirm-export-without-alt-title" className="confirm-dialog-title">
              Export Without Alt Text?
            </h3>
            <p id="confirm-export-without-alt-message" className="confirm-dialog-message">
              You have not generated alt text for this image. Are you sure you want to export without alt text?
            </p>
            <label className="confirm-dialog-checkbox">
              <input
                type="checkbox"
                checked={dontAskExportWithoutAltAgain}
                onChange={(e) => setDontAskExportWithoutAltAgain(e.target.checked)}
              />
              <span>Do not ask again in the future</span>
            </label>
            <div className="confirm-dialog-buttons">
              <Button onClick={cancelExportWithoutAltText}>Cancel</Button>
              <Button variant="primary" onClick={confirmExportWithoutAltText}>
                Export Anyway
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      <Toast
        message={toastMessage}
        visible={showToast}
        icon={<CheckIcon />}
      />
    </>
  );
}
